import React, { useState } from 'react';
import { Pencil, Star } from 'lucide-react';
import type { Order, PoS, Product } from '../../types/index.js';
import { formatDate, hasRealDate } from '../../utils/datetime.js';
import {
  parseJsonValue,
  placementSnapshotLabels,
} from '../../utils/visitProductSummary.js';
import { ALL_STORE_TYPES } from './StoreTypeSelector.js';
import { VisitProductChips } from './VisitProductChips.js';
import { EditPoSSheet } from './EditPoSSheet.js';
import { posAddressLine, useVisitBriefing } from './useVisitBriefing.js';

const OUTCOME_LABELS: Record<string, string> = {
  placement_secured: 'Placement secured',
  rejected: 'Rejected',
  follow_up_needed: 'Follow-up needed',
  partial_placement: 'Partial placement',
  competitor_exclusive: 'Competitor exclusive',
};

interface VisitBriefingCardProps {
  pos: PoS;
  products: Product[];
  onPosUpdated: (pos: PoS) => void;
}

export const VisitBriefingCard: React.FC<VisitBriefingCardProps> = ({
  pos,
  products,
  onPosUpdated,
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const briefing = useVisitBriefing(pos.id);
  const storeType =
    ALL_STORE_TYPES.find((t) => t.value === pos.store_type)?.label ||
    pos.store_type;

  return (
    <div className="p-4 bg-brand-50 border border-brand-200 rounded-xl space-y-4">
      <Header pos={pos} storeType={storeType} onEdit={() => setEditOpen(true)} />
      <ContactBlock pos={pos} />
      {briefing.loading && (
        <p className="text-sm text-brand-700">Loading last visit...</p>
      )}
      {!briefing.loading && briefing.lastVisit && (
        <LastVisitBlock
          visitDate={briefing.lastVisit.visit_date || briefing.lastVisit.created_at}
          visitType={briefing.lastVisit.visit_type}
          outcome={briefing.lastVisit.visit_outcome}
          notes={briefing.lastVisit.notes}
          salesChecked={
            Object.keys(
              parseJsonValue<Record<string, unknown>>(
                briefing.lastVisit.sales_performance,
                {}
              )
            ).length > 0
          }
        />
      )}
      {!briefing.loading && !briefing.lastVisit && (
        <p className="text-sm text-brand-700">No previous visit recorded.</p>
      )}
      {briefing.lastVisit && (
        <HistoryDetails
          lastVisit={briefing.lastVisit}
          orders={briefing.orders}
          products={products}
        />
      )}
      <DistributorBlock distributors={briefing.distributors} />
      {!pos.invoice_email && (
        <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Invoice email missing — needed before sending a delivery note.
        </p>
      )}
      <PinnedNotes notes={briefing.pinnedNotes.map((n) => n.body)} />
      <EditPoSSheet
        open={editOpen}
        pos={pos}
        onClose={() => setEditOpen(false)}
        onSaved={onPosUpdated}
      />
    </div>
  );
};

function Header({
  pos,
  storeType,
  onEdit,
}: {
  pos: PoS;
  storeType: string;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-brand-900 text-lg">{pos.name}</p>
          {pos.is_premium && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
              <Star size={12} className="fill-amber-400 text-amber-400" />
              Premium
            </span>
          )}
        </div>
        <p className="text-sm text-brand-700">{storeType}</p>
        <p className="text-sm text-brand-700">{posAddressLine(pos)}</p>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="min-h-[44px] px-3 rounded-xl bg-white border border-brand-300 text-brand-800 text-sm font-bold inline-flex items-center gap-1.5"
      >
        <Pencil size={14} />
        Edit PoS
      </button>
    </div>
  );
}

function ContactBlock({ pos }: { pos: PoS }) {
  const person = [
    pos.contact_person_name,
    pos.contact_person_position,
  ].filter(Boolean).join(' · ');
  const times = pos.contact_availability_time || pos.preferred_contact_time;
  if (!person && !pos.phone && !pos.email && !pos.contact_person_phone && !times) {
    return null;
  }
  return (
    <div className="text-sm text-brand-900 space-y-0.5">
      {person && <p className="font-semibold">{person}</p>}
      {pos.contact_person_phone && (
        <a href={`tel:${pos.contact_person_phone}`} className="block underline">
          {pos.contact_person_phone}
        </a>
      )}
      {pos.phone && pos.phone !== pos.contact_person_phone && (
        <a href={`tel:${pos.phone}`} className="block underline">{pos.phone}</a>
      )}
      {pos.contact_person_email && (
        <a href={`mailto:${pos.contact_person_email}`} className="block underline">
          {pos.contact_person_email}
        </a>
      )}
      {pos.email && pos.email !== pos.contact_person_email && (
        <a href={`mailto:${pos.email}`} className="block underline">{pos.email}</a>
      )}
      {times && <p className="text-xs text-brand-700">Best time: {times}</p>}
    </div>
  );
}

function LastVisitBlock({
  visitDate,
  visitType,
  outcome,
  notes,
  salesChecked,
}: {
  visitDate?: string;
  visitType?: string;
  outcome?: string;
  notes?: string;
  salesChecked: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const typeLabel = visitType === 'first_time' ? 'First visit' : 'Revisit';
  const outcomeLabel = outcome ? OUTCOME_LABELS[outcome] || outcome : null;
  const truncated = notes && notes.length > 140 && !expanded;
  return (
    <div className="text-sm space-y-1">
      <p className="font-bold text-brand-900">Last visit</p>
      <p className="text-brand-800">
        {hasRealDate(visitDate) ? formatDate(visitDate) : 'Unknown date'}
        {' · '}
        {typeLabel}
        {outcomeLabel ? ` · ${outcomeLabel}` : ''}
      </p>
      {salesChecked && (
        <p className="text-xs text-brand-700">Stock was checked on that visit.</p>
      )}
      {notes && (
        <div>
          <p className="text-brand-800 whitespace-pre-wrap">
            {truncated ? `${notes.slice(0, 140)}…` : notes}
          </p>
          {notes.length > 140 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs font-bold text-brand-700 underline"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryDetails({
  lastVisit,
  orders,
  products,
}: {
  lastVisit: { visit_products?: unknown; product_placements?: unknown; product_id?: string };
  orders: Order[];
  products: Product[];
}) {
  const placementLabels = placementSnapshotLabels(lastVisit.product_placements);
  return (
    <div className="space-y-2">
      <VisitProductChips
        visitProducts={lastVisit.visit_products}
        productPlacements={lastVisit.product_placements}
        fallbackProductId={lastVisit.product_id}
        products={products}
        showSecuredBadge
      />
      {placementLabels.length > 0 && (
        <p className="text-xs text-brand-800">
          Placements: {placementLabels.join(', ')}
        </p>
      )}
      {orders.length > 0 && (
        <ul className="text-xs text-brand-900 space-y-1">
          {orders.map((order) => (
            <li key={order.id}>
              {order.quantity}× {order.product_name || 'Product'}
              {order.variant_name ? ` (${order.variant_name})` : ''}
              {' · '}
              {order.distributor_is_self
                ? 'Own Delivery'
                : order.distributor_name || 'Distributor'}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DistributorBlock({
  distributors,
}: {
  distributors: Array<{
    distributor_id: string;
    distributor_name?: string;
    pos_number_at_distributor: string;
    is_primary: boolean;
  }>;
}) {
  if (distributors.length === 0) return null;
  return (
    <div className="text-xs text-brand-900 space-y-1">
      <p className="font-bold">Distributors</p>
      {distributors.map((d) => (
        <p key={d.distributor_id}>
          {d.distributor_name || 'Distributor'}
          {d.is_primary ? ' (primary)' : ''}
          {d.pos_number_at_distributor
            ? ` · ${d.pos_number_at_distributor}`
            : ''}
        </p>
      ))}
    </div>
  );
}

function PinnedNotes({ notes }: { notes: string[] }) {
  if (notes.length === 0) return null;
  return (
    <div className="text-xs text-brand-900 space-y-1">
      <p className="font-bold">Pinned notes</p>
      {notes.map((body, i) => (
        <p key={i} className="whitespace-pre-wrap">{body}</p>
      ))}
    </div>
  );
}
